import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { updatePaymentSelectFields } from '@/features/paymentSelect/paymentSelectSlice';
import { logger } from '@/services/logger/logger';
import { useCartContext } from '@/services/cart/CartContext';

import journeysData from './journeys.json';
import { ActionName } from './types';
import {
  getCurrentJourney,
  loadJourney,
  determineCurrentStep,
  getJourneyStep,
  getStepAction,
  checkConditionalRules,
} from './utils';
import { FULFILMENT_TYPES } from '@/constants/constants';

const INDEX_STEP = '<INDEX>';

export function useJourneyNavigate(overrideCurrentStep?: string) {
  const dispatch = useAppDispatch();
  const journeysState = useAppSelector(state => state.journeys);
  const shippingDetailsState = useAppSelector(state => state.shippingDetails);
  const { getCartFulfilmentType } = useCartContext();

  const location = useLocation();
  const navigate = useNavigate();

  const journeyNavigate = useCallback((action: ActionName) => {
    async function doJourneyNavigate() {
      try {
        const isAARN = getCartFulfilmentType() === FULFILMENT_TYPES.AARN;
        const currentJourneyName = getCurrentJourney(journeysState);
        const currentJourney = loadJourney(journeysData, currentJourneyName);
        const currentStepName = determineCurrentStep(currentJourney, location, isAARN, overrideCurrentStep);
        const currentStep = getJourneyStep(currentJourney, currentStepName);
        const { basePath, basePathAdopt } = currentJourney;

        // Auto-detect AARN from URL path if cart fulfilment type is not set
        const shouldUseAARN = isAARN || Boolean(basePathAdopt && location.pathname.startsWith(basePathAdopt));

        // Tweak the navigate function params dynamically based on the context:
        const thisBasePath = shouldUseAARN && basePathAdopt ? basePathAdopt : basePath;

        // Check conditional rules for dynamic navigation
        const conditionalStep = checkConditionalRules(
          currentJourney,
          currentStepName,
          action,
          shouldUseAARN,
          (shippingDetailsState.postalRewards) || false
        );

        const nextStep = conditionalStep ?? getStepAction(currentStep, action);

        if (nextStep === INDEX_STEP) {
          await navigate('/');
        } else {
          await navigate(`${thisBasePath}${nextStep}`);
        }

        if (nextStep === 'payment-select') {
          dispatch(updatePaymentSelectFields({ provider: undefined }));
        }
      } catch (error) {
        logger.error('Error while navigating to step...');
        logger.error(error);
        await navigate('/not-found');
      }
    }

    void doJourneyNavigate();
  }, [dispatch, journeysState, overrideCurrentStep, location, navigate, shippingDetailsState.postalRewards, getCartFulfilmentType]);

  return journeyNavigate;
}
